/** The views' edit-mode marks (plan §1.1, D1): per registry group — a spine stage or a
 *  tab of the tabs view — the group's unsaved changes and how many of them the restech's
 *  next update will undo. One draft, so both views count the same changes. */
import { useMemo } from 'react';

import type { BookingRecordFacet, BookingRecordStage } from '@/api/bookingRecord';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';

import { BOOKING_RECORD_REGISTRY, SPINE_STAGES, type SpineStage } from '../registry';

import type { StageMarks } from './SpineNav';

/** Edit-mode marks per registry group — per stage (the spine's nodes) or per facet (the
 *  tabs view's tabs): the group's unsaved changes and how many of them the restech will
 *  undo. */
export function useSectionMarks<K extends 'stage' | 'facet'>(
  by: K,
): Partial<Record<K extends 'stage' ? BookingRecordStage : BookingRecordFacet, StageMarks>> {
  const { state } = useDraft();
  const { fieldForPath, config } = useRecordEditor();
  return useMemo(() => {
    const out: Partial<Record<string, StageMarks>> = {};
    for (const [rec, paths] of Object.entries(state.entries)) {
      for (const path of Object.keys(paths)) {
        const field = fieldForPath(path, rec);
        const group = field ? BOOKING_RECORD_REGISTRY[field.key as keyof typeof BOOKING_RECORD_REGISTRY]?.[by] : undefined;
        if (!field || !group) continue;
        const m = (out[group] ??= { count: 0, reverts: 0 });
        m.count += 1;
        if (config.verdictOf(rec, field, state)?.verdict === 'overwrites') m.reverts += 1;
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [by, state.version, fieldForPath, config]);
}

/** Edit-mode marks per spine stage. Changes in the Communication lane have no node. */
export function useSpineMarks(): Partial<Record<SpineStage, StageMarks>> {
  const marks = useSectionMarks('stage');
  return useMemo(
    () => Object.fromEntries(SPINE_STAGES.flatMap((s) => (marks[s] ? [[s, marks[s]]] : []))),
    [marks],
  );
}
